import { AlertTriangle, CheckCircle2, Info, LoaderCircle, X, XCircle } from 'lucide-react';
import { Component, useEffect } from 'react';
import { useUI } from '../context/UIContext';

export class PageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Page render failed', error, info);
  }

  componentDidUpdate(previousProps) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Panel>
        <ErrorState error={this.state.error} onRetry={() => this.setState({ error: null })} />
      </Panel>
    );
  }
}

export function PageHeader({ title, description, icon: Icon, actions }) {
  return (
    <header className="page-header">
      <div className="page-heading">
        {Icon && <Icon size={20} aria-hidden="true" />}
        <div>
          <h1>{title}</h1>
          {description && <p>{description}</p>}
        </div>
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Panel({ title, meta, actions, children, className = '' }) {
  return (
    <section className={`panel ${className}`}>
      {(title || meta || actions) && (
        <header className="panel-header">
          <div>
            {title && <h2>{title}</h2>}
            {meta && <span className="panel-meta">{meta}</span>}
          </div>
          {actions && <div className="panel-actions">{actions}</div>}
        </header>
      )}
      <div className="panel-body">{children}</div>
    </section>
  );
}

export function Stat({ label, value, detail, tone = 'neutral', icon: Icon }) {
  return (
    <div className={`stat stat-${tone}`}>
      {Icon && <span className="stat-icon"><Icon size={18} /></span>}
      <div>
        <span className="stat-label">{label}</span>
        <strong className="stat-value">{value}</strong>
        {detail && <span className="stat-detail">{detail}</span>}
      </div>
    </div>
  );
}

export function Badge({ children, tone = 'neutral' }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function StatusBadge({ status }) {
  const normalized = String(status || 'offline').toLowerCase();
  const tone = normalized === 'online' || normalized === 'active' ? 'success'
    : normalized === 'degraded' || normalized === 'mitigated' ? 'warning'
      : normalized === 'ended' ? 'neutral' : 'danger';
  return <Badge tone={tone}><span className="status-dot" />{normalized}</Badge>;
}

export function EmptyState({ title = 'No data', detail, icon: Icon = Info }) {
  return (
    <div className="empty-state">
      <Icon size={24} />
      <strong>{title}</strong>
      {detail && <span>{detail}</span>}
    </div>
  );
}

export function Loading({ label = 'Loading...' }) {
  return <div className="loading"><LoaderCircle className="spin" size={18} />{label}</div>;
}

export function ErrorState({ error, onRetry }) {
  return (
    <div className="error-state" role="alert">
      <AlertTriangle size={20} />
      <span>{error?.message || 'Unable to load data'}</span>
      {onRetry && <button className="button button-secondary button-small" onClick={onRetry}>Retry</button>}
    </div>
  );
}

export function Modal({ title, description, size = 'medium', className = '', onClose, children, footer }) {
  useEffect(() => {
    const onKey = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className={`modal modal-${size} ${className}`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <header className="modal-header">
          <div>
            <h2 id="modal-title">{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <button className="icon-button" onClick={onClose} title="Close"><X size={18} /></button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </section>
    </div>
  );
}

export function ConfirmDialog({ title, message, confirmLabel = 'Confirm', danger = false, onConfirm, onClose, busy }) {
  return (
    <Modal title={title} onClose={onClose} size="small" footer={(
      <>
        <button className="button button-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className={`button ${danger ? 'button-danger' : 'button-primary'}`} onClick={onConfirm} disabled={busy}>
          {busy && <LoaderCircle className="spin" size={16} />}{confirmLabel}
        </button>
      </>
    )}>
      <p className="confirm-message">{message}</p>
    </Modal>
  );
}

export function Toast() {
  const { toast, clearToast } = useUI();
  useEffect(() => {
    if (!toast) return undefined;
    const timeout = setTimeout(clearToast, 5000);
    return () => clearTimeout(timeout);
  }, [clearToast, toast]);
  if (!toast) return null;
  const icons = { success: CheckCircle2, danger: XCircle, warning: AlertTriangle, info: Info };
  const Icon = icons[toast.type] || Info;
  return (
    <div className={`toast toast-${toast.type}`} role="status">
      <Icon size={19} />
      <div><strong>{toast.title}</strong><span>{toast.message}</span></div>
      <button className="icon-button" onClick={clearToast} title="Dismiss"><X size={16} /></button>
    </div>
  );
}

export function Toggle({ checked, onChange, label, disabled = false }) {
  return (
    <label className={`toggle ${disabled ? 'is-disabled' : ''}`}>
      <input type="checkbox" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} disabled={disabled} />
      <span className="toggle-track"><span /></span>
      {label && <span>{label}</span>}
    </label>
  );
}

export function Segmented({ value, onChange, options, label }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} className={value === option.value ? 'active' : ''} onClick={() => onChange(option.value)} type="button">
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function DataTable({ columns, rows, keyForRow, empty = 'No records', onRowClick }) {
  if (!rows?.length) return <EmptyState title={empty} />;
  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead><tr>{columns.map((column) => <th key={column.key} className={column.align ? `align-${column.align}` : ''}>{column.label}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => (
          <tr key={keyForRow ? keyForRow(row) : index} onClick={onRowClick ? () => onRowClick(row) : undefined} className={onRowClick ? 'clickable' : ''}>
            {columns.map((column) => <td key={column.key} className={column.align ? `align-${column.align}` : ''}>{column.render ? column.render(row) : row[column.key]}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}
